import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../store/hooks';
import { login } from '../store/slices/authSlice';
import jwtService from '../auth/jwt/jwtService';

const Login: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        // Check for error params
        const errorParam = searchParams.get('error');
        if (errorParam === 'insufficient_privileges') {
            setError('Access denied. Only administrators can access this portal.');
        }
    }, [searchParams]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            // Call the actual backend API
            const response = await jwtService.login({ email, password });

            // Dispatch login action with real data from backend
            dispatch(login({
                user: {
                    id: response.user.id,
                    email: response.user.email,
                    name: `${response.user.firstName} ${response.user.lastName}`,
                    role: response.user.role,
                    avatar: response.user.profileImage || undefined,
                },
                token: response.accessToken
            }));

            // Navigate to dashboard
            navigate('/');
        } catch (err: any) {
            console.error('Login error:', err);
            setError(err.message || 'Login failed. Please check your credentials and try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-background min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5" style={{
                backgroundColor: '#1e293b',
                backgroundImage: 'radial-gradient(#334155 1px, transparent 1px)',
                backgroundSize: '20px 20px'
            }}></div>

            {/* Gradient Blobs */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>

            <div className="relative z-10 w-full max-w-4xl grid lg:grid-cols-2 gap-0 bg-white rounded-2xl shadow-xl overflow-hidden">

                {/* Left Panel - Admin Branding */}
                <div className="hidden lg:flex flex-col justify-between p-8 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 relative overflow-hidden">

                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-10" style={{
                        backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                    }}></div>

                    {/* Gradient Blobs */}
                    <div className="absolute top-10 right-10 w-48 h-48 bg-blue-500/20 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-10 left-10 w-48 h-48 bg-blue-500/20 rounded-full blur-3xl"></div>

                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-6">
                            <img src="/logo.png" alt="Enjoy Triathlon" className="w-32 h-auto object-contain" />
                        </div>

                        <h2 className="text-3xl font-bold text-white mb-3 leading-tight">
                            Admin Access
                        </h2>
                        <p className="text-slate-300 text-sm font-medium leading-relaxed">
                            Sign in to manage users and weekly training plans.
                        </p>
                    </div>

                    <div className="relative z-10 space-y-4">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center text-blue-400 flex-shrink-0">
                                <i className="fa-solid fa-users-gear text-lg"></i>
                            </div>
                            <div>
                                <h4 className="text-white font-bold text-sm mb-0.5">User Management</h4>
                                <p className="text-slate-400 text-xs">Manage users and trainers</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center text-blue-400 flex-shrink-0">
                                <i className="fa-solid fa-calendar-check text-lg"></i>
                            </div>
                            <div>
                                <h4 className="text-white font-bold text-sm mb-0.5">Training Assignment</h4>
                                <p className="text-slate-400 text-xs">Assign and copy weekly exercises</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Panel - Login Form */}
                <div className="p-8 flex flex-col justify-center">

                    {/* Mobile Logo */}
                    <div className="lg:hidden mb-6 text-center">
                        <div className="flex items-center justify-center gap-3 mb-3">
                            <img src="/logo.png" alt="Enjoy Triathlon" className="w-32 h-auto object-contain" />
                        </div>
                        <h3 className="text-slate-800 font-bold text-lg">Admin Portal</h3>
                    </div>

                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-slate-800 mb-1">Admin Sign In</h1>
                        <p className="text-slate-500 text-sm font-medium">Enter your administrator credentials</p>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="mb-4 p-3 bg-blue-50 border-l-4 border-cyan-500 rounded-lg">
                            <div className="flex items-start gap-2">
                                <i className="fa-solid fa-circle-exclamation text-cyan-600 text-sm mt-0.5"></i>
                                <div>
                                    <h4 className="text-xs font-bold text-blue-900 mb-0.5">Access Denied</h4>
                                    <p className="text-xs text-blue-700">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">

                        {/* Email Field */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">
                                Admin Email <span className="text-cyan-500">*</span>
                            </label>
                            <div className="relative">
                                <i className="fa-solid fa-envelope absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none"></i>
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="admin@enjoyTriathlon.com"
                                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border-2 border-cyan-200 rounded-lg text-sm font-medium text-slate-700 focus:outline-none focus:bg-white focus:border-cyan-500 transition-all"
                                />
                            </div>
                        </div>

                        {/* Password Field */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">
                                Password <span className="text-cyan-500">*</span>
                            </label>
                            <div className="relative">
                                <i className="fa-solid fa-lock absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none"></i>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    className="w-full pl-10 pr-11 py-3 bg-slate-50 border-2 border-cyan-200 rounded-lg text-sm font-medium text-slate-700 focus:outline-none focus:bg-white focus:border-cyan-500 transition-all"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                    <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-sm`}></i>
                                </button>
                            </div>
                        </div>

                        {/* Security Notice */}
                        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <i className="fa-solid fa-shield-halved text-blue-600 text-sm mt-0.5"></i>
                            <div>
                                <h4 className="text-xs font-bold text-blue-900 mb-0.5">Restricted Access</h4>
                                <p className="text-xs text-blue-700">This portal is restricted to authorized administrators only.</p>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full py-3.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white rounded-lg font-bold text-sm shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transition-all duration-300 border-b-4 border-blue-700 active:border-b-0 active:translate-y-1 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <>
                                    <i className="fa-solid fa-spinner fa-spin"></i>
                                    <span>SIGNING IN...</span>
                                </>
                            ) : (
                                <>
                                    <span>SIGN IN</span>
                                    <i className="fa-solid fa-arrow-right"></i>
                                </>
                            )}
                        </button>
                    </form>

                    {/* Footer Links */}
                    <div className="mt-6 pt-6 border-t border-slate-200">
                        <div className="flex items-center justify-center gap-4 text-slate-400">
                            <i className="fa-solid fa-shield-halved text-sm"></i>
                            <span className="text-xs font-medium">Admin Access</span>
                            <span className="text-slate-300">•</span>
                            <span className="text-xs font-medium">© 2026 Enjoy Triathlon</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
